import { useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { cn } from '@mds/shared/lib/utils';
import type { AdminBooking } from '@mds/shared/types/booking';

import {
  bookingStatusLabel,
  bookingStatusTone,
  bookingSpace,
  bookingTime,
} from '@/domain/bookings';
import { WEEKDAYS, addMonths, keyOf, monthGrid, monthTitle, sameDay } from '@mds/shared/lib/dates';

/** How many bookings a day shows before it offers the rest in the list. */
const SHOWN_PER_DAY = 3;

/** One day's bookings as the page read them. */
export interface CalendarDay {
  bookings: AdminBooking[];
  /** Everything booked that day, which can be more than one page returned. */
  total: number;
  pending: boolean;
  failed: boolean;
}

const focusRing =
  'focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)';

/** Rooms by start time, with the day-long desk bookings first, then by space. */
const byTime = (a: AdminBooking, b: AdminBooking) =>
  (a.startMinute ?? -1) - (b.startMinute ?? -1) || a.resource.name.localeCompare(b.resource.name);

/**
 * The bookings page as a month: every day of it, with what is booked on each.
 *
 * Each day shows its first few bookings in time order, coloured by status, and
 * any more as a count that opens the day in the list. Days outside the month
 * are drawn but not read, so the grid keeps its six-week shape without asking
 * for bookings nobody is looking at.
 */
export default function BookingsMonth({
  month,
  onMonthChange,
  days,
  onOpen,
  onShowDay,
}: {
  /** Any date in the month shown. */
  month: Date;
  onMonthChange: (month: Date) => void;
  /** Keyed by `YYYY-MM-DD`, for the days of `month` only. */
  days: Record<string, CalendarDay>;
  onOpen: (booking: AdminBooking) => void;
  /** Opens one day in the list view, for a day with more than fits. */
  onShowDay: (dateKey: string) => void;
}) {
  const cells = useMemo(() => monthGrid(month), [month]);
  const today = new Date();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <NavButton label="Previous month" onClick={() => onMonthChange(addMonths(month, -1))}>
            <ChevronLeftIcon className="size-5" aria-hidden="true" />
          </NavButton>
          <NavButton label="Next month" onClick={() => onMonthChange(addMonths(month, 1))}>
            <ChevronRightIcon className="size-5" aria-hidden="true" />
          </NavButton>
        </div>
        <h3 className="m-0 text-xl font-semibold tracking-tight">{monthTitle(month)}</h3>
        <button
          type="button"
          onClick={() => onMonthChange(new Date())}
          className={cn(
            'min-h-(--target-control) rounded-xl border border-hairline bg-surface px-4 text-base font-semibold text-ink-secondary transition-colors hover:border-brand-edge hover:text-brand-text',
            focusRing,
          )}
        >
          Today
        </button>
      </div>

      {/* Seven readable columns need room; on a phone the grid scrolls sideways
          rather than squeezing a booking into a sliver. */}
      <div className="overflow-x-auto">
        <div className="grid min-w-[56rem] grid-cols-7 gap-1.5">
          {WEEKDAYS.map((d) => (
            <div
              key={d}
              className="pb-1 text-center font-mono text-sm font-semibold tracking-wide text-ink-secondary uppercase"
            >
              {d}
            </div>
          ))}

          {cells.map((d) => {
            const key = keyOf(d);
            const outside = d.getMonth() !== month.getMonth();
            const isToday = sameDay(d, today);
            const day = outside ? undefined : days[key];
            const sorted = day ? [...day.bookings].sort(byTime) : [];
            const more = day ? day.total - Math.min(sorted.length, SHOWN_PER_DAY) : 0;

            return (
              <div
                key={key}
                className={cn(
                  'flex min-h-32 flex-col gap-1 rounded-xl border p-2',
                  outside
                    ? 'border-transparent bg-transparent'
                    : isToday
                      ? 'border-brand-edge bg-brand-wash'
                      : 'border-hairline bg-surface',
                )}
              >
                <span
                  className={cn(
                    'text-base tabular-nums',
                    outside
                      ? 'text-ink-muted'
                      : isToday
                        ? 'font-semibold text-brand-strong'
                        : 'text-ink-secondary',
                  )}
                  aria-current={isToday ? 'date' : undefined}
                >
                  {d.getDate()}
                </span>

                {day?.pending && <span className="text-sm text-ink-muted">…</span>}
                {day?.failed && (
                  <span className="text-sm text-danger-text">Could not load this day.</span>
                )}

                {sorted.slice(0, SHOWN_PER_DAY).map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => onOpen(b)}
                    title={`${b.customer.name} · ${bookingSpace(b)} · ${bookingTime(b)} · ${bookingStatusLabel(b.status)}`}
                    className={cn(
                      'flex flex-col items-start rounded-lg border px-2 py-1 text-left text-sm transition-colors hover:brightness-95',
                      bookingStatusTone(b.status),
                      focusRing,
                    )}
                  >
                    <span className="w-full truncate font-mono text-xs tracking-wide uppercase">
                      {bookingTime(b)}
                    </span>
                    <span className="w-full truncate font-semibold">{b.customer.name}</span>
                    <span className="w-full truncate">{bookingSpace(b)}</span>
                  </button>
                ))}

                {more > 0 && (
                  <button
                    type="button"
                    onClick={() => onShowDay(key)}
                    className={cn(
                      'self-start rounded-md px-1 text-sm font-semibold text-brand-text hover:underline',
                      focusRing,
                    )}
                  >
                    +{more} more
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-12 items-center justify-center rounded-xl border border-hairline bg-surface text-ink-secondary transition-colors hover:border-brand-edge hover:text-brand-text',
        focusRing,
      )}
    >
      {children}
    </button>
  );
}
